import { LinkButton } from '@atlaskit/button/new';

import { RovoLogo } from '@atlaskit/logo';

import { token } from '@atlaskit/tokens';

import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { JiraYourWorkFirstPartyRecommendation } from '@post-office/components--jira-your-work-first-party-recommendation';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';

import { useCallback, useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { createRender } from './types';

import { type JSX } from 'react/jsx-runtime';

const CCP_PRODUCT_ID = '91ae3e3f-138e-43be-98cc-48df03d13c32';

const HAMS_PRODUCT_ID = 'rovo.ondemand';

const WATCH_LINK =
	'https://events.atlassian.com/team-digital/v/s-2980720?utm_medium=in-product&utm_source=jira&event_context=701qb00000lg6obyar&utm_campaign=K:event%7CH:digital%7CN:team_25%7CP:agnostic%7CO:event-marketing%7CF:post-event%7CC:event-home&utm_content=in-product-messaging&event_id=216653';

const whyAmISeeingThisModalContent = {
	message: (
		<p>
			<FormattedMessage
				id="growth.team-25-ipm.jira-your-work.reasons-modal.message"
				defaultMessage="Atlassian recently shared multiple announcements at Team '25, and we wanted to make sure you know about our new capabilities."
				description="Explanation of why we think the Rovo recommendation will be relevant to the customer."
			/>
		</p>
	),
	reasons: [],
	privacyPolicy: <></>,
};

export type Props = {
	onDismissed: (messageInstanceId: string) => void;
};

export const render: (renderProps: { onDismissed: () => void }) => JSX.Element | null =
	createRender(({ onDismissed }: Props) => {
		const intl = useIntl();
		const { colorMode } = useThemeObserver();
		const { messageClicked, messageViewed } = useMessageEvent();
		const { messageInstanceViewed } = useMessageInstanceEvent();
		const { onCtaActioned } = usePostOfficeUserInteractions();

		const handleClick = useCallback(() => {
			messageClicked().catch(() => {});
			void onCtaActioned({ interactionIdentifier: 'cta' });
		}, [messageClicked, onCtaActioned]);

		useEffect(() => {
			void messageInstanceViewed();
			void messageViewed();
		}, [messageInstanceViewed, messageViewed]);

		return (
			<ManualMessageViewedEvent>
				<JiraYourWorkFirstPartyRecommendation
					data-testid="jira-your-work-rovo-team-25-message"
					onDismissed={onDismissed}
					whyAmISeeingThisModalContent={whyAmISeeingThisModalContent}
					app={{
						product: 'Rovo Team 25',
						hamsIdentifier: HAMS_PRODUCT_ID,
						ccpProductId: CCP_PRODUCT_ID,
						logo: <RovoLogo size="small" appearance="brand" />,
						header: (
							<FormattedMessage
								id="growth.team-25-ipm.jira-your-work.title"
								description="Title for Rovo Team 25 message in Jira Your Work"
								defaultMessage="Work better with Rovo"
							/>
						),
						description: (
							<FormattedMessage
								id="growth.team-25-ipm.jira-your-work.description"
								description="Description for Rovo Team 25 message in Jira Your Work"
								defaultMessage="See how Rovo can improve the way you work with AI search, chat, and agents."
							/>
						),
						actions: (
							<LinkButton href={WATCH_LINK} appearance="default" onClick={handleClick}>
								<FormattedMessage
									id="growth.team-25-ipm.jira-your-work.call-to-action.watch-now.text"
									description="Description for the call to action button to watch the video announcement"
									defaultMessage="Watch now"
								/>
							</LinkButton>
						),
						headerImageAltText: intl.formatMessage({
							id: 'growth.team-25-ipm.jira-your-work.header-image-alt-text',
							description:
								'Two people fist-bumping in celebration with the headline, "Rovo is for everyone!"',
							defaultMessage: 'See the Rovo announcement!',
						}),
					}}
					iconColor={colorMode === 'dark' ? token('color.icon') : token('color.icon.inverse')}
				/>
			</ManualMessageViewedEvent>
		);
	});

export default render;
